import React, { useCallback, useEffect, useMemo, useState } from 'react';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import logStyles from '../PriceChangeLogs/PriceChangeLogs.module.css';
import scopeStyles from './SyncVerificationLogs.module.css';
import {
  fetchSyncVerificationLogs,
  runSyncVerification,
  type SyncVerificationLogRow,
} from './api/syncVerificationLogs.api';

const PAGE_SIZE = 50;

const FIELD_OPTIONS = [
  { value: '', label: 'All fields' },
  { value: 'production_id', label: 'Production ID' },
  { value: 'barcode', label: 'Barcode' },
  { value: 'seat', label: 'Section / Row / Seat' },
  { value: 'marketplaces', label: 'Marketplaces' },
  { value: 'shared', label: 'Shared status' },
  { value: 'price', label: 'Price' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'Success', label: 'Success' },
  { value: 'Failure', label: 'Failure' },
];

function formatWhen(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function statusBadgeClass(status: SyncVerificationLogRow['status']): string {
  return String(status || '').toLowerCase() === 'failure'
    ? logStyles.badgeFailure
    : logStyles.badgeSuccess;
}

function uniqueTpIds(rows: SyncVerificationLogRow[]): string[] {
  const ids = rows
    .map((row) => String(row.tpId || '').trim())
    .filter(Boolean);
  return [...new Set(ids)];
}

export type SyncVerificationLogsPanelProps = {
  tpId?: string;
  tpIds?: string[];
  eventId?: string;
  compact?: boolean;
  hideFilters?: boolean;
  hideRunButton?: boolean;
  refreshSignal?: number;
};

const SyncVerificationLogsPanel: React.FC<SyncVerificationLogsPanelProps> = ({
  tpId,
  tpIds,
  compact = false,
  hideFilters = false,
  hideRunButton = false,
  refreshSignal = 0,
}) => {
  const [logs, setLogs] = useState<SyncVerificationLogRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [field, setField] = useState('');
  const [status, setStatus] = useState('Failure');
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runMessage, setRunMessage] = useState<string | null>(null);
  const [checkedTpIds, setCheckedTpIds] = useState<string[]>([]);

  const scopedTpIds = useMemo(() => {
    const ids = [
      ...(Array.isArray(tpIds) ? tpIds : []),
      ...(tpId ? [tpId] : []),
    ]
      .map((id) => String(id || '').trim())
      .filter(Boolean);
    return [...new Set(ids)];
  }, [tpId, tpIds]);

  const scopedTpIdsKey = scopedTpIds.join(',');

  const pageTpIds = useMemo(() => uniqueTpIds(logs), [logs]);

  const runTargetTpIds = useMemo(() => {
    if (scopedTpIds.length) return scopedTpIds;
    return [...new Set(checkedTpIds.map((id) => String(id || '').trim()).filter(Boolean))];
  }, [scopedTpIds, checkedTpIds]);

  const canRunSelected =
    runTargetTpIds.length > 0 && !running && !loading;

  const runButtonLabel = useMemo(() => {
    if (running) return 'Running…';
    if (runTargetTpIds.length) {
      return `Run verification (${runTargetTpIds.length} ticket${runTargetTpIds.length === 1 ? '' : 's'})`;
    }
    return 'Run verification';
  }, [running, runTargetTpIds.length]);

  const allPageSelected =
    pageTpIds.length > 0 && pageTpIds.every((id) => checkedTpIds.includes(id));

  const somePageSelected =
    pageTpIds.some((id) => checkedTpIds.includes(id)) && !allPageSelected;

  const toggleTpId = (tpIdValue: string) => {
    const id = String(tpIdValue || '').trim();
    if (!id) return;
    setCheckedTpIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const selectAllOnPage = () => {
    setCheckedTpIds((prev) => [...new Set([...prev, ...pageTpIds])]);
  };

  const clearSelection = () => setCheckedTpIds([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchSyncVerificationLogs({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        search: hideFilters ? '' : search,
        field: hideFilters ? '' : field,
        status: hideFilters ? '' : status,
        tpIds: scopedTpIds.length ? scopedTpIds : undefined,
      });
      setLogs(result.logs || []);
      setTotalCount(result.totalCount || 0);
    } catch (err: any) {
      setError(err?.message || 'Failed to load sync verification logs');
      setLogs([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, search, field, status, hideFilters, scopedTpIds, scopedTpIdsKey]);

  useEffect(() => {
    void load();
  }, [load, refreshSignal]);

  useEffect(() => {
    setPage(0);
  }, [scopedTpIdsKey]);

  const handleRunVerification = async (verifyAllListed = false) => {
    if (!verifyAllListed && !runTargetTpIds.length) {
      setError('Check one or more tickets in the table, then run verification');
      return;
    }

    setRunning(true);
    setRunMessage(null);
    setError(null);
    try {
      const result = await runSyncVerification({
        ticketPlacementIds: verifyAllListed ? undefined : runTargetTpIds,
        verifyAllListed,
      });
      setRunMessage(result.message);
      setPage(0);
      await load();
    } catch (err: any) {
      setError(err?.message || 'Sync verification failed');
    } finally {
      setRunning(false);
    }
  };

  const toggleAllOnPage = () => {
    if (allPageSelected) {
      setCheckedTpIds((prev) => prev.filter((id) => !pageTpIds.includes(id)));
      return;
    }
    selectAllOnPage();
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const rangeLabel = useMemo(() => {
    if (!totalCount) return '0 logs';
    const from = page * PAGE_SIZE + 1;
    const to = Math.min(totalCount, (page + 1) * PAGE_SIZE);
    return `${from}–${to} of ${totalCount}`;
  }, [page, totalCount]);

  const showSelectionColumn = !scopedTpIds.length;

  return (
    <div className={compact ? logStyles.panelCompact : undefined}>
      {!hideFilters && (
        <div className={logStyles.toolbar}>
          <input
            className={logStyles.searchInput}
            placeholder="Search event, seat, field, client, failure reason…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setPage(0);
                setSearch(searchInput.trim());
              }
            }}
          />
          <select
            className={logStyles.selectInput}
            value={field}
            onChange={(e) => {
              setPage(0);
              setField(e.target.value);
            }}
          >
            {FIELD_OPTIONS.map((opt) => (
              <option key={opt.value || 'all-fields'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            className={logStyles.selectInput}
            value={status}
            onChange={(e) => {
              setPage(0);
              setStatus(e.target.value);
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || 'all-statuses'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={logStyles.refreshBtn}
            disabled={loading}
            onClick={() => {
              setPage(0);
              setSearch(searchInput.trim());
              void load();
            }}
          >
            <RefreshRoundedIcon fontSize="small" />
            Refresh
          </button>
          {!hideRunButton && (
            <button
              type="button"
              className={logStyles.refreshBtn}
              disabled={!canRunSelected}
              onClick={() => void handleRunVerification(false)}
              title="Re-check selected tickets against Victory Live"
            >
              <PlayArrowRoundedIcon fontSize="small" />
              {runButtonLabel}
            </button>
          )}
        </div>
      )}

      {hideFilters && (
        <div className={logStyles.toolbar}>
          <button
            type="button"
            className={logStyles.refreshBtn}
            disabled={loading}
            onClick={() => void load()}
          >
            <RefreshRoundedIcon fontSize="small" />
            Refresh
          </button>
          {!hideRunButton && (
            <button
              type="button"
              className={logStyles.refreshBtn}
              disabled={!canRunSelected}
              onClick={() => void handleRunVerification(false)}
            >
              <PlayArrowRoundedIcon fontSize="small" />
              {runButtonLabel}
            </button>
          )}
        </div>
      )}

      {showSelectionColumn && !hideFilters && logs.length > 0 && (
        <div className={scopeStyles.selectionActions}>
          <button type="button" className={scopeStyles.linkBtn} onClick={selectAllOnPage}>
            Select all on page
          </button>
          <button
            type="button"
            className={scopeStyles.linkBtn}
            onClick={clearSelection}
            disabled={!checkedTpIds.length}
          >
            Clear selection
          </button>
          <span className={scopeStyles.selectionSummary}>
            {runTargetTpIds.length
              ? `${runTargetTpIds.length} ticket${runTargetTpIds.length === 1 ? '' : 's'} selected`
              : 'Check tickets below, then Run verification'}
          </span>
          {!hideRunButton && (
            <button
              type="button"
              className={scopeStyles.linkBtn}
              disabled={running || loading}
              onClick={() => void handleRunVerification(true)}
              title="Verify all VL-linked tickets (max 500)"
            >
              Or run all VL-linked
            </button>
          )}
        </div>
      )}

      {runMessage ? (
        <div className={logStyles.muted} style={{ marginBottom: 12 }}>
          {runMessage}
        </div>
      ) : null}

      <div className={logStyles.card}>
        {loading ? (
          <div className={logStyles.loading}>Loading sync verification logs…</div>
        ) : error ? (
          <div className={logStyles.error}>{error}</div>
        ) : logs.length === 0 ? (
          <div className={logStyles.empty}>
            No sync failures found. Failures from VL share, price sync, and verification appear here
            automatically.
          </div>
        ) : (
          <div className={logStyles.tableWrap}>
            <table className={logStyles.table}>
              <thead>
                <tr>
                  {showSelectionColumn ? (
                    <th className={scopeStyles.checkboxCell}>
                      <input
                        type="checkbox"
                        checked={allPageSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = somePageSelected;
                        }}
                        onChange={toggleAllOnPage}
                        aria-label="Select all tickets on this page"
                        disabled={!pageTpIds.length}
                      />
                    </th>
                  ) : null}
                  <th>When</th>
                  <th>Field</th>
                  <th>VL action</th>
                  <th>Event / Seat</th>
                  <th>QuickAsyst</th>
                  <th>Victory Live</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((row) => {
                  const rowTpId = String(row.tpId || '').trim();
                  const selectable = showSelectionColumn && Boolean(rowTpId);
                  const checked = rowTpId ? checkedTpIds.includes(rowTpId) : false;
                  return (
                    <tr key={row.id}>
                      {showSelectionColumn ? (
                        <td className={scopeStyles.checkboxCell}>
                          {selectable ? (
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleTpId(rowTpId)}
                              aria-label={`Select ${row.seatLabel || row.eventName || 'ticket'}`}
                            />
                          ) : null}
                        </td>
                      ) : null}
                      <td>
                        <div className={logStyles.stack}>
                          <span>{formatWhen(row.createdAt)}</span>
                          {row.verifiedByName ? (
                            <span className={logStyles.muted}>by {row.verifiedByName}</span>
                          ) : null}
                          {row.tpStatus ? (
                            <span className={logStyles.muted}>{row.tpStatus}</span>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <span className={`${logStyles.badge} ${logStyles.badgeDefault}`}>
                          {row.fieldLabel}
                        </span>
                      </td>
                      <td>
                        <span className={logStyles.muted}>
                          {row.vlAction || row.triggerSource || '—'}
                        </span>
                      </td>
                      <td>
                        <div className={logStyles.stack}>
                          <span>{row.eventName || '—'}</span>
                          <span className={logStyles.muted}>
                            {row.seatLabel ||
                              [
                                row.section && `Sec ${row.section}`,
                                row.row && `Row ${row.row}`,
                                row.seat != null && `Seat ${row.seat}`,
                              ]
                                .filter(Boolean)
                                .join(' · ') ||
                              '—'}
                          </span>
                          {row.venue ? <span className={logStyles.muted}>{row.venue}</span> : null}
                          {row.clientName ? (
                            <span className={logStyles.muted}>{row.clientName}</span>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <span className={logStyles.muted}>{row.qaValue || '—'}</span>
                      </td>
                      <td>
                        <span className={logStyles.muted}>{row.vlValue || '—'}</span>
                      </td>
                      <td>
                        <div className={logStyles.stack}>
                          <span className={`${logStyles.badge} ${statusBadgeClass(row.status)}`}>
                            {row.status}
                          </span>
                          {row.status === 'Failure' && row.systemsNote ? (
                            <span className={logStyles.muted}>{row.systemsNote}</span>
                          ) : null}
                          {row.status === 'Failure' && row.failureReason ? (
                            <span className={logStyles.muted}>{row.failureReason}</span>
                          ) : null}
                          {row.status === 'Failure' && row.authoritativeSystem ? (
                            <span className={logStyles.muted}>
                              Likely correct:{' '}
                              {row.authoritativeSystem === 'quickasyst'
                                ? 'QuickAsyst'
                                : 'Victory Live'}
                            </span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className={logStyles.footer}>
          <span className={logStyles.muted}>{rangeLabel}</span>
          <div className={logStyles.pager}>
            <button
              type="button"
              disabled={loading || page <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </button>
            <span className={logStyles.muted}>
              Page {page + 1} / {totalPages}
            </span>
            <button
              type="button"
              disabled={loading || page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SyncVerificationLogsPanel;
